import React, { useEffect, useRef, useState } from "react"
import axios from "axios"
// Revue 6 (🟠9) : plus d'import d'Addons (cycle d'imports → MA_VERSION
// undefined sur /ecran).
import {
	MA_VERSION,
	nouveauBundleServi,
	pageServie,
	recharger,
	rechargementActif,
	rechargementAutoPermis,
} from "../../utilities/versionDeploiement"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faExpand, faWifi } from "@fortawesome/free-solid-svg-icons"
import { URL } from "../../store/actions/depannageActions"
import { pagesDisponibles } from "./EcranSlides"
import { T, TAILLE, dateDuJour, heureMinute } from "./ecranUtils"

// Écran « salle de pause » (télétablette, /ecran). Page AUTONOME montée par
// src/index.js hors d'App : ni NavV2, ni Redux, ni bootstrap, ni socket.
// Accès par clé d'écran (`?cle=` à la 1re ouverture → localStorage), jamais
// par compte (cf. backend svp/ecran.py).

const POLL_MS = 60 * 1000
// Une page reste 30 s ; une page choisie au doigt reste 60 s.
const ROTATION_MS = 30 * 1000
const DUREE_APRES_TAP_MS = 60 * 1000
const FONDU_MS = 300
const CLE_STORAGE = "ecran_cle"
const POLICE_DM_SANS =
	"https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;700&display=swap"

// Clé : `?cle=` mémorisée puis retirée de l'URL (ni barre d'adresse, ni
// historique) ; sinon celle déjà mémorisée. Les autres paramètres restent.
const lireCle = () => {
	const params = new URLSearchParams(window.location.search)
	const depuisUrl = params.get("cle")
	if (depuisUrl) {
		let memorisee = false
		try {
			localStorage.setItem(CLE_STORAGE, depuisUrl)
			memorisee = localStorage.getItem(CLE_STORAGE) === depuisUrl
		} catch (e) {
			// Stockage indisponible : voir ci-dessous.
		}
		// Non mémorisée : la clé reste dans l'URL, sinon le rechargement de
		// 04:00 la perdait (« Écran non configuré » chaque matin).
		if (!memorisee) return depuisUrl
		params.delete("cle")
		const reste = params.toString()
		window.history.replaceState(
			null,
			"",
			window.location.pathname + (reste ? `?${reste}` : ""),
		)
		return depuisUrl
	}
	try {
		return localStorage.getItem(CLE_STORAGE) || ""
	} catch (e) {
		return ""
	}
}

// Prochain 04:00 local : rechargement quotidien (prend les nouveaux builds).
const prochainRechargement = () => {
	const cible = new Date()
	cible.setHours(4, 0, 0, 0)
	if (cible <= new Date()) cible.setDate(cible.getDate() + 1)
	return cible.getTime()
}

// QA en dev : `?date=AAAA-MM-JJ` affiche l'écran tel qu'à cette date (le
// serveur l'ignore en prod).
const lireApercu = () =>
	new URLSearchParams(window.location.search).get("date") || ""

// Données d'un autre jour que maintenant (aperçu de QA) : à signaler.
const dateDesDonnees = (data, maintenant) => {
	const genere = data && data.live && data.live.genere_le
	if (!genere) return null
	const d = new Date(genere)
	return Math.abs(d - maintenant) > 3600 * 1000 ? d : null
}

const estPleinEcran = () =>
	!!(document.fullscreenElement || document.webkitFullscreenElement)

const mouvementReduit = () =>
	!!(
		window.matchMedia &&
		window.matchMedia("(prefers-reduced-motion: reduce)").matches
	)

const EcranPage = () => {
	const [cle] = useState(lireCle)
	const [apercu] = useState(lireApercu)
	const [data, setData] = useState(null)
	// "chargement" | "ok" | "refuse" | "sans_cle"
	const [statut, setStatut] = useState(cle ? "chargement" : "sans_cle")
	const [messageRefus, setMessageRefus] = useState("")
	const [horsLigneDepuis, setHorsLigneDepuis] = useState(null)
	const [maintenant, setMaintenant] = useState(() => new Date())
	const [index, setIndex] = useState(0)
	const [indexAffiche, setIndexAffiche] = useState(0)
	const [opacite, setOpacite] = useState(1)
	const [pleinEcran, setPleinEcran] = useState(estPleinEcran)
	// Début et durée de la page en cours (la rotation et sa barre de
	// progression en découlent).
	const debutPageRef = useRef(Date.now())
	const dureePageRef = useRef(ROTATION_MS)
	const rechargementRef = useRef(prochainRechargement())
	// Revue 7 (Re4) : vérification de la page en cours avant le rechargement.
	const verificationRef = useRef(false)
	const horsLigneRef = useRef(null)
	horsLigneRef.current = horsLigneDepuis

	const pages = pagesDisponibles(data)
	const nbPages = pages.length

	// Page au thème de l'app + DM Sans (chargée ici seulement).
	useEffect(() => {
		document.title = "Écran salle de pause"
		document.body.style.margin = "0"
		document.body.style.backgroundColor = T.fond
		document.body.style.overflow = "hidden"
		if (!document.getElementById("ecran-police")) {
			const lien = document.createElement("link")
			lien.id = "ecran-police"
			lien.rel = "stylesheet"
			lien.href = POLICE_DM_SANS
			document.head.appendChild(lien)
		}
		// Installée depuis /ecran (« Ajouter à l'écran d'accueil »), l'app
		// doit rouvrir l'écran : manifeste dédié (start_url ./ecran) au lieu
		// de celui de l'app (start_url « . » → la page d'accueil).
		const manifeste = document.querySelector('link[rel="manifest"]')
		if (manifeste)
			manifeste.href = `${process.env.PUBLIC_URL}/manifest-ecran.json`
	}, [])

	// Polling 60 s. Échec réseau/serveur : on garde les dernières données et
	// on note « hors ligne depuis » (pas de spinner). Refus (clé) : arrêt net,
	// aucune boucle de requêtes — le rechargement de 04:00 retentera.
	useEffect(() => {
		if (!cle) return undefined
		let vivant = true
		let enVol = false
		const charger = () => {
			if (enVol) return
			enVol = true
			axios
				.get(`${URL}api/ecran`, {
					headers: { Authorization: `Ecran ${cle}` },
					params: apercu ? { maintenant: apercu } : undefined,
					timeout: 20000,
				})
				.then((res) => {
					if (!vivant) return
					// Nouvelle version déployée : le TV recharge (aucune saisie
					// possible ; une seule fois par version) — revue 6 (R1)
					// seulement si /ecran sert VRAIMENT un autre bundle (jamais
					// sur une page d'erreur pendant le deploy) ; sinon réessai
					// au sondage suivant, l'écran continue d'afficher.
					const deployee =
						res.headers && res.headers["x-svpapp-version"]
					if (
						deployee &&
						rechargementActif() &&
						deployee !== MA_VERSION &&
						rechargementAutoPermis(deployee)
					) {
						nouveauBundleServi(window.location.pathname).then(
							(nouveau) => {
								if (nouveau && vivant) recharger(deployee)
							},
						)
					}
					setData(res.data)
					setHorsLigneDepuis(null)
					setStatut("ok")
				})
				.catch((err) => {
					if (!vivant) return
					const code = err.response && err.response.status
					if (code === 401 || code === 403) {
						clearInterval(minuterie)
						setMessageRefus(
							(err.response.data && err.response.data.message) ||
								"",
						)
						setStatut("refuse")
						return
					}
					setHorsLigneDepuis((d) => d || new Date())
				})
				.then(() => {
					enVol = false
				})
		}
		const minuterie = setInterval(charger, POLL_MS)
		charger()
		return () => {
			vivant = false
			clearInterval(minuterie)
		}
	}, [cle, apercu])

	// Horloge (1 s) : heure affichée, rotation des pages, rechargement de
	// 04:00 (vérifié à chaque tick plutôt qu'un setTimeout de 24 h, que le
	// navigateur peut mettre en veille). Hors ligne, il attend le retour du
	// réseau : recharger sans serveur laisserait la page d'erreur du
	// navigateur à la place de l'écran (les dernières données restent).
	useEffect(() => {
		const t = setInterval(() => {
			const now = new Date()
			setMaintenant(now)
			if (
				now.getTime() >= rechargementRef.current &&
				navigator.onLine !== false &&
				!horsLigneRef.current &&
				!verificationRef.current
			) {
				// Revue 7 (Re4) : seulement si /ecran répond (jamais vers un
				// 502) ; sinon réessai 30 s plus tard.
				verificationRef.current = true
				pageServie(window.location.pathname).then((ok) => {
					if (ok) window.location.reload()
					else
						setTimeout(() => {
							verificationRef.current = false
						}, 30000)
				})
			}
		}, 1000)
		return () => clearInterval(t)
	}, [])

	useEffect(() => {
		if (nbPages < 2) return
		const t = maintenant.getTime()
		if (t - debutPageRef.current >= dureePageRef.current) {
			debutPageRef.current = t
			dureePageRef.current = ROTATION_MS
			setIndex((i) => i + 1)
		}
	}, [maintenant, nbPages])

	// Fondu entre deux pages (sauf si l'appareil demande moins de mouvement).
	const cible = nbPages ? index % nbPages : 0
	useEffect(() => {
		if (cible === indexAffiche) {
			// Cible revenue sur la page affichée pendant un fondu (données
			// rafraîchies, tap) : le timeout est annulé, il faut rallumer —
			// sinon l'écran restait noir (opacité 0).
			setOpacite(1)
			return undefined
		}
		if (mouvementReduit()) {
			setIndexAffiche(cible)
			return undefined
		}
		setOpacite(0)
		const t = setTimeout(() => {
			setIndexAffiche(cible)
			setOpacite(1)
		}, FONDU_MS)
		return () => clearTimeout(t)
	}, [cible, indexAffiche])

	// Écran toujours allumé (HTTPS requis), redemandé au retour au premier plan.
	useEffect(() => {
		let verrou = null
		const demander = () => {
			if (
				!("wakeLock" in navigator) ||
				document.visibilityState !== "visible"
			)
				return
			navigator.wakeLock
				.request("screen")
				.then((v) => {
					verrou = v
				})
				.catch(() => {})
		}
		demander()
		document.addEventListener("visibilitychange", demander)
		return () => {
			document.removeEventListener("visibilitychange", demander)
			if (verrou) verrou.release().catch(() => {})
		}
	}, [])

	useEffect(() => {
		const suivre = () => setPleinEcran(estPleinEcran())
		document.addEventListener("fullscreenchange", suivre)
		document.addEventListener("webkitfullscreenchange", suivre)
		return () => {
			document.removeEventListener("fullscreenchange", suivre)
			document.removeEventListener("webkitfullscreenchange", suivre)
		}
	}, [])

	const demanderPleinEcran = (e) => {
		e.stopPropagation()
		const el = document.documentElement
		const demande = el.requestFullscreen || el.webkitRequestFullscreen
		if (!demande) return
		const r = demande.call(el)
		if (r && r.catch) r.catch(() => {})
	}

	// Choix au doigt : la page choisie reste 60 s, puis la rotation reprend.
	const allerA = (i) => {
		debutPageRef.current = Date.now()
		dureePageRef.current = DUREE_APRES_TAP_MS
		setIndex(i)
	}
	const tap = () => {
		if (nbPages > 1) allerA(cible + 1)
	}

	if (statut === "sans_cle" || statut === "refuse")
		return (
			<div style={st.ecran}>
				<div style={st.centre}>
					<div style={st.carteMessage}>
						<h1 style={st.messageTitre}>Écran non configuré</h1>
						<p style={st.messageTexte}>
							{statut === "refuse"
								? "Le serveur a refusé la clé de cet écran."
								: "Cet écran n'a pas encore de clé."}{" "}
							Ouvrez l'adresse complète fournie par
							l'administrateur : elle se termine par « ?cle=… ».
						</p>
						{statut === "refuse" && messageRefus ? (
							<p style={st.messageDetail}>{messageRefus}</p>
						) : null}
					</div>
				</div>
			</div>
		)

	const affichee = nbPages ? pages[Math.min(indexAffiche, nbPages - 1)] : null
	const autreJour = dateDesDonnees(data, maintenant)
	const progression = Math.min(
		1,
		Math.max(0, maintenant.getTime() - debutPageRef.current) /
			dureePageRef.current,
	)

	return (
		<div style={st.ecran}>
			<header style={st.entete}>
				<div style={st.gauche}>
					<span style={st.date}>{dateDuJour(maintenant)}</span>
					{autreJour ? (
						<span style={st.apercu}>
							Aperçu : données du {dateDuJour(autreJour)}
						</span>
					) : null}
				</div>
				{nbPages > 1 ? (
					<nav style={st.onglets} aria-label="Pages de l'écran">
						{pages.map((p, i) => {
							const actif = i === cible
							return (
								<button
									key={p.cle}
									type="button"
									style={st.onglet(actif)}
									onClick={() => allerA(i)}
								>
									{p.nom}
									{actif ? (
										<span
											style={{
												...st.ongletProgression,
												transform: `scaleX(${progression})`,
											}}
										/>
									) : null}
								</button>
							)
						})}
					</nav>
				) : (
					<span />
				)}
				<div style={st.droite}>
					{horsLigneDepuis ? (
						<span style={st.horsLigne}>
							<FontAwesomeIcon icon={faWifi} /> Hors ligne depuis{" "}
							{heureMinute(horsLigneDepuis)}
						</span>
					) : null}
					<span style={st.heure}>{heureMinute(maintenant)}</span>
					{!pleinEcran ? (
						<button
							type="button"
							style={st.boutonPleinEcran}
							onClick={demanderPleinEcran}
							title="Plein écran"
							aria-label="Plein écran"
						>
							<FontAwesomeIcon icon={faExpand} />
						</button>
					) : null}
				</div>
			</header>

			<main
				style={{ ...st.scene, opacity: opacite }}
				onClick={tap}
				aria-live="polite"
			>
				{affichee ? (
					affichee.rendu()
				) : (
					<div style={st.centre}>
						<p style={st.attente}>
							{statut === "chargement"
								? "Connexion au serveur…"
								: "Les chiffres apparaîtront ici dès le premier véhicule accueilli."}
						</p>
					</div>
				)}
			</main>
		</div>
	)
}

const st = {
	ecran: {
		position: "fixed",
		top: 0,
		right: 0,
		bottom: 0,
		left: 0,
		display: "flex",
		flexDirection: "column",
		gap: "3vh",
		backgroundColor: T.fond,
		color: T.encre,
		fontFamily: T.typo,
		padding: "3vh 4vw 4vh",
		boxSizing: "border-box",
		overflow: "hidden",
		userSelect: "none",
		WebkitFontSmoothing: "antialiased",
	},
	entete: {
		display: "grid",
		gridTemplateColumns: "1fr auto 1fr",
		alignItems: "center",
		gap: "2vw",
	},
	gauche: {
		display: "flex",
		alignItems: "center",
		gap: "1.2vw",
		minWidth: 0,
	},
	date: {
		fontSize: TAILLE.texte,
		fontWeight: 500,
		color: T.discret,
		whiteSpace: "nowrap",
	},
	apercu: {
		padding: "0.6vh 1vw",
		borderRadius: 999,
		backgroundColor: T.bleuFond,
		color: T.bleu,
		fontSize: TAILLE.petit,
		fontWeight: 700,
		whiteSpace: "nowrap",
	},
	onglets: {
		display: "inline-flex",
		gap: 4,
		padding: 6,
		backgroundColor: T.fond,
		border: `1px solid ${T.trait}`,
		borderRadius: 14,
	},
	onglet: (actif) => ({
		position: "relative",
		overflow: "hidden",
		padding: "1vh 2.2vw",
		borderRadius: 10,
		border: "none",
		fontFamily: "inherit",
		fontSize: TAILLE.petit,
		fontWeight: actif ? 700 : 500,
		cursor: "pointer",
		whiteSpace: "nowrap",
		color: actif ? T.encre : T.discret,
		backgroundColor: actif ? T.surface : "transparent",
		boxShadow: actif ? "0 1px 3px rgba(28, 25, 23, 0.08)" : "none",
	}),
	// Remplissage du segment actif : temps restant avant la page suivante.
	ongletProgression: {
		position: "absolute",
		left: 0,
		right: 0,
		bottom: 0,
		height: 4,
		backgroundColor: T.encre,
		transformOrigin: "left center",
		transition: "transform 1s linear",
	},
	droite: {
		display: "flex",
		alignItems: "center",
		justifyContent: "flex-end",
		gap: "1.6vw",
	},
	horsLigne: {
		display: "flex",
		alignItems: "center",
		gap: 10,
		padding: "0.8vh 1.2vw",
		borderRadius: 999,
		backgroundColor: T.orangeFond,
		color: T.orange,
		fontSize: TAILLE.petit,
		fontWeight: 700,
		whiteSpace: "nowrap",
	},
	heure: {
		fontSize: TAILLE.grand,
		fontWeight: 700,
		fontVariantNumeric: "tabular-nums",
		letterSpacing: "-0.01em",
	},
	boutonPleinEcran: {
		width: "5vh",
		height: "5vh",
		minWidth: 40,
		minHeight: 40,
		borderRadius: 12,
		border: `1px solid ${T.trait}`,
		backgroundColor: T.surface,
		color: T.discret,
		fontSize: TAILLE.petit,
		cursor: "pointer",
	},
	scene: {
		flex: 1,
		minHeight: 0,
		cursor: "pointer",
		transition: `opacity ${FONDU_MS}ms ease`,
	},
	centre: {
		height: "100%",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
	},
	carteMessage: {
		maxWidth: "60vw",
		padding: "4vh 3vw",
		borderRadius: 20,
		backgroundColor: T.surface,
		border: `1px solid ${T.trait}`,
	},
	messageTitre: {
		margin: "0 0 2vh",
		fontSize: TAILLE.titre,
		fontWeight: 700,
	},
	messageTexte: {
		margin: 0,
		fontSize: TAILLE.texte,
		lineHeight: 1.4,
		color: T.encre,
	},
	messageDetail: {
		margin: "2vh 0 0",
		fontSize: TAILLE.petit,
		color: T.discret,
	},
	attente: {
		margin: 0,
		fontSize: TAILLE.grand,
		color: T.discret,
		textAlign: "center",
		maxWidth: "60vw",
	},
}

export default EcranPage
